// Domain types for the push-notification pipe.
// See docs/adr/0001-push-notifications-outbox.md and
// docs/adr/0003-lifecycle-notifications.md.
//
// This module is the SINGLE shared vocabulary for notifications, imported by the
// backend, the worker, and the frontend. Keep it framework-agnostic — no Fastify
// or React imports. It must always agree with docs/notifications/notification-catalogue.md.

/**
 * The catalogue of shipped notification types. Each type here has a matching
 * entry in notification-catalogue.md and a `category` + `coalesces` entry below.
 * Candidate types stay documented in the catalogue but intentionally NOT listed
 * here until they ship.
 */
export const NOTIFICATION_TYPES = [
  // v1 (shipped / first)
  "WISHLISTED_ANIMAL_ADOPTED",
  "NEW_MATCHES_PAWMATCH",
  "WEEKLY_DIGEST",
  // v1.x
  "FIRST_LIKE_NUDGE",
  "COMEBACK_PAWMATCH",
  "PAWMATCH_DAILY_SWIPES_READY",
  "FIRST_SAVED_SEARCH_NUDGE",
  // Wishlisted-animal lifecycle (transactional; recipients = Wishlist rows)
  "WISHLISTED_ANIMAL_AVAILABLE",
  // PawMatch matched-animal lifecycle (transactional; recipients = Pawmatch MATCH rows)
  "PAWMATCH_MATCH_ADOPTED",
  "PAWMATCH_MATCH_AVAILABLE",
  // Publisher/admin operational tasks
  "PUBLISHER_MISSING_CHIP_NUMBERS",
  // Approved Particular must finish Publisher Training before publishing new animals
  "PUBLISHER_TRAINING_REQUIRED",
  // Explicit saved-search alert subscriptions (user-created filters)
  "SAVED_SEARCH_NEW_ANIMALS",
  // Adoption application lifecycle (Part 4)
  "ADOPTION_APPLICATION_RECEIVED",
  "ADOPTION_APPLICATION_STATUS_CHANGED",
  // Adoption in-app chat (Part 3)
  "ADOPTION_MESSAGE_RECEIVED",
  // Post-adoption check-up reminders (Part 5)
  "ADOPTION_CHECKUP_REMINDER",
  // Adoption progress checklist (Part 7)
  "ADOPTION_STEP_CHANGED",
  // Kennel-initiated manual chat for already-adopted animals (Part 7)
  "ADOPTION_MANUAL_CHAT_CREATED",
] as const

export type NotificationType = (typeof NOTIFICATION_TYPES)[number]

export function isNotificationType(value: string): value is NotificationType {
  return (NOTIFICATION_TYPES as readonly string[]).includes(value)
}

/**
 * Category drives the frequency cap (see ADR 0003):
 * - `transactional` — highest intent; BYPASSES the marketing/engagement daily cap
 *   (still respects quiet hours + per-type opt-out).
 * - `engagement` — personalised activity; subject to the ≤ 1 push/day cap.
 * - `marketing` — digests / win-back; subject to the ≤ 1 push/day cap. No weekly cap.
 */
export type NotificationCategory = "transactional" | "engagement" | "marketing"

export const NOTIFICATION_CATEGORY: Record<NotificationType, NotificationCategory> = {
  WISHLISTED_ANIMAL_ADOPTED: "transactional",
  NEW_MATCHES_PAWMATCH: "engagement",
  WEEKLY_DIGEST: "marketing",
  FIRST_LIKE_NUDGE: "engagement",
  COMEBACK_PAWMATCH: "marketing",
  PAWMATCH_DAILY_SWIPES_READY: "engagement",
  FIRST_SAVED_SEARCH_NUDGE: "engagement",
  WISHLISTED_ANIMAL_AVAILABLE: "transactional",
  PAWMATCH_MATCH_ADOPTED: "transactional",
  PAWMATCH_MATCH_AVAILABLE: "transactional",
  PUBLISHER_MISSING_CHIP_NUMBERS: "transactional",
  PUBLISHER_TRAINING_REQUIRED: "transactional",
  SAVED_SEARCH_NEW_ANIMALS: "transactional",
  ADOPTION_APPLICATION_RECEIVED: "transactional",
  ADOPTION_APPLICATION_STATUS_CHANGED: "transactional",
  ADOPTION_MESSAGE_RECEIVED: "transactional",
  ADOPTION_CHECKUP_REMINDER: "transactional",
  ADOPTION_STEP_CHANGED: "transactional",
  ADOPTION_MANUAL_CHAT_CREATED: "transactional",
}

/**
 * Whether a type coalesces: ONE rolling Inbox Item per user per type, merged in
 * place as new entities arrive (rather than N separate items). Windowed one-shot
 * types (e.g. daily/lapsed nudges) are NOT coalescing — they are deduped by window
 * but never merge multiple entities into one rolling item.
 */
export const NOTIFICATION_COALESCES: Record<NotificationType, boolean> = {
  WISHLISTED_ANIMAL_ADOPTED: false,
  NEW_MATCHES_PAWMATCH: true,
  WEEKLY_DIGEST: true,
  FIRST_LIKE_NUDGE: false,
  COMEBACK_PAWMATCH: false,
  PAWMATCH_DAILY_SWIPES_READY: false,
  FIRST_SAVED_SEARCH_NUDGE: false,
  WISHLISTED_ANIMAL_AVAILABLE: false,
  PAWMATCH_MATCH_ADOPTED: false,
  PAWMATCH_MATCH_AVAILABLE: false,
  PUBLISHER_MISSING_CHIP_NUMBERS: true,
  PUBLISHER_TRAINING_REQUIRED: false,
  SAVED_SEARCH_NEW_ANIMALS: true,
  ADOPTION_APPLICATION_RECEIVED: false,
  ADOPTION_APPLICATION_STATUS_CHANGED: false,
  ADOPTION_MESSAGE_RECEIVED: true,
  ADOPTION_CHECKUP_REMINDER: true,
  ADOPTION_STEP_CHANGED: true,
  ADOPTION_MANUAL_CHAT_CREATED: false,
}

/** True when the type bypasses the marketing/engagement daily frequency cap. */
export function bypassesFrequencyCap(type: NotificationType): boolean {
  return NOTIFICATION_CATEGORY[type] === "transactional"
}

/** The user-visible content + deep-link data stored on an outbox row. */
export interface NotificationPayload {
  title: string
  body: string
  /** String map forwarded to FCM `data` (all values must be strings). */
  data: Record<string, string>
}

export type NotificationStatus = "PENDING" | "SENT" | "FAILED" | "SKIPPED"

export type DevicePlatform = "android" | "ios"
